import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import ApplicationLayout from 'v3/layouts/Application';
import { DataTable, Text, Button } from 'v3/components';
import { useSnackbar } from 'v3/components/Snackbar';
import useQuery from 'hooks/useQuery';
import SingleChecklistResponseFilter from './Filter/index';
import AdvancedFilterModal from './Filter/AdvancedFilterModal/index';
import apiReport from 'services/apis/report';
import FileSaver from 'file-saver';

export default function SingleChecklistResponseMaintenance() {
  const MAX_INTERVAL_DAYS = 90;
  const snackbar = useSnackbar();
  const queryParams = useQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [data, setData] = useState([]);
  const [qtdAdvancedFilters, setQtdAdvancedFilters] = useState(0);
  const [showAdvancedFiltersModal, setShowAdvancedFiltersModal] = useState(false);

  const [pagination, setPagination] = useState({
    total: 0,
    lastPage: 1,
    currentPage: 1,
  });

  const [advancedFilters, setAdvancedFilters] = useState({
    start_date: null,
    end_date: null,
    expired: false,
    status: null,
    vehicles: [],
    wagons: [],
    page: 1,
    per_page: 10,
    idSearch: null,
    expireInDays: null,
  });

  function handleTitleAdvancedFilters() {
    let title = 'Filtro avançado';
    if (qtdAdvancedFilters) {
      title += ` (${qtdAdvancedFilters})`;
    }
    return title;
  }

  function normalizeAndValidateDateRange(filters) {
    let startDate = filters?.start_date ? moment(filters.start_date).format('YYYY-MM-DD') : null;
    let endDate = filters?.end_date ? moment(filters.end_date).format('YYYY-MM-DD') : null;

    if (!startDate && !endDate) {
      endDate = moment().format('YYYY-MM-DD');
      startDate = moment(endDate).subtract(MAX_INTERVAL_DAYS, 'days').format('YYYY-MM-DD');
    } else if (startDate && !endDate) {
      endDate = moment().format('YYYY-MM-DD');
    } else if (!startDate && endDate) {
      startDate = moment(endDate).subtract(MAX_INTERVAL_DAYS, 'days').format('YYYY-MM-DD');
    }

    const intervalDays = moment(endDate).diff(moment(startDate), 'days');
    if (intervalDays > MAX_INTERVAL_DAYS) {
      return {
        isValid: false,
        filters: {
          ...filters,
          start_date: startDate,
          end_date: endDate,
        },
      };
    }

    return {
      isValid: true,
      filters: {
        ...filters,
        start_date: startDate,
        end_date: endDate,
      },
    };
  }

  function showDateRangeWarning() {
    snackbar.show(<Text>O intervalo de datas não pode ser maior que 90 dias.</Text>, { type: 'warning' });
  }

  function syncMainFiltersQuery(filters) {
    const nextQuery = new URLSearchParams(location.search);

    if (filters?.start_date) {
      nextQuery.set('start_date', moment(filters.start_date).format('YYYY-MM-DD'));
    } else {
      nextQuery.delete('start_date');
    }

    if (filters?.end_date) {
      nextQuery.set('end_date', moment(filters.end_date).format('YYYY-MM-DD'));
    } else {
      nextQuery.delete('end_date');
    }

    if (filters?.status?.id) {
      nextQuery.set('status', String(filters.status.id));
    } else {
      nextQuery.delete('status');
    }

    if (filters?.page) {
      nextQuery.set('page', String(filters.page));
    }

    if (filters?.per_page) {
      nextQuery.set('per_page', String(filters.per_page));
    }

    navigate({ search: nextQuery.toString() }, { replace: true });
  }

  const exportReport = async () => {
    const { isValid, filters: normalizedFilters } = normalizeAndValidateDateRange(advancedFilters);

    if (!isValid) {
      showDateRangeWarning();
      return;
    }

    try {
      setLoadingExport(true);

      const params = new URLSearchParams();

      params.append('page', normalizedFilters?.page);
      params.append('per_page', normalizedFilters?.per_page);

      if (normalizedFilters?.start_date) {
        params.append('start_date', moment(normalizedFilters.start_date).format('YYYY-MM-DD'));
      }
      if (normalizedFilters?.end_date) {
        params.append('end_date', moment(normalizedFilters.end_date).format('YYYY-MM-DD'));
      }
      if (normalizedFilters?.status) {
        params.append('status', normalizedFilters?.status?.id);
      }

      if (normalizedFilters?.vehicles?.length) {
        normalizedFilters.vehicles.forEach(vehicle => {
          params.append('vehicles[]', vehicle.id);
        });
      }

      if (normalizedFilters?.wagons?.length) {
        normalizedFilters.wagons.forEach(wagon => {
          params.append('wagons[]', wagon.id);
        });
      }

      if (normalizedFilters?.idSearch) {
        params.append('idSearch', normalizedFilters.idSearch);
      }

      if (normalizedFilters?.expireInDays) {
        params.append('expireInDays', normalizedFilters.expireInDays);
      }

      const response = await apiReport.get(`/export-single-checklist-response?${params.toString()}`, { responseType: 'blob' });
      FileSaver.saveAs(response.data, response.headers['x-file-name']);

    } catch (error) {
      setLoadingExport(false);
      const message = 'Ocorreu um erro ao exportar o relatório.';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setLoadingExport(false);
    }
  }

  const fetchData = useCallback(async (advancedFilters) => {
    const { isValid, filters: normalizedFilters } = normalizeAndValidateDateRange(advancedFilters);

    if (!isValid) {
      showDateRangeWarning();
      return;
    }

    setLoading(true);
    try {
      // Construir query params
      const params = new URLSearchParams();
      params.append('page', normalizedFilters?.page);
      params.append('per_page', normalizedFilters?.per_page);

      if (normalizedFilters?.start_date) {
        params.append('start_date', moment(normalizedFilters.start_date).format('YYYY-MM-DD'));
      }
      if (normalizedFilters?.end_date) {
        params.append('end_date', moment(normalizedFilters.end_date).format('YYYY-MM-DD'));
      }
      if (normalizedFilters?.status) {
        params.append('status', normalizedFilters?.status?.id);
      }

      if (normalizedFilters?.vehicles?.length) {
        normalizedFilters.vehicles.forEach(vehicle => {
          params.append('vehicles[]', vehicle.id);
        });
      }

      if (normalizedFilters?.wagons?.length) {
        normalizedFilters.wagons.forEach(wagon => {
          params.append('wagons[]', wagon.id);
        });
      }

      if (normalizedFilters?.idSearch) {
        params.append('idSearch', normalizedFilters.idSearch);
      }

      if (normalizedFilters?.expireInDays) {
        params.append('expireInDays', normalizedFilters.expireInDays);
      }

      // Chamar API do report-api
      const response = await apiReport.get(`/single-checklist-response?${params.toString()}`);

      const { items = [], pagination = {} } = response.data;

      setPagination({
        total: pagination.total || 0,
        lastPage: pagination.lastPage || 1,
        currentPage: normalizedFilters.page,
      });
      setData(items);
    } catch (error) {
      setData([]);
      const message = error?.response?.data?.message || 'Ocorreu um erro ao carregar o relatório.';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [snackbar]);

  async function changePage(page) {
    const newFilters = { ...advancedFilters, page };
    syncMainFiltersQuery(newFilters);
    setAdvancedFilters(newFilters);
    fetchData(newFilters);
  }

  async function changePerPage(per_page) {
    const newFilters = { ...advancedFilters, per_page };
    syncMainFiltersQuery(newFilters);
    setAdvancedFilters(newFilters);
    fetchData(newFilters);
  }

  // Inicializar filtros a partir de queryParams
  useEffect(() => {
    const statusId = queryParams?.get('status');

    const newFilters = {
      start_date: queryParams?.get('start_date') || null,
      end_date: queryParams?.get('end_date') || null,
      page: queryParams?.get('page') ? Number(queryParams.get('page')) : 1,
      per_page: queryParams?.get('per_page') ? Number(queryParams.get('per_page')) : 10,
      status: statusId ? { id: Number(statusId) } : null,
      vehicles: queryParams?.getAll('vehicles[]')?.length ? queryParams.getAll('vehicles[]').map(id => ({ id: Number(id) })) : [],
      wagons: queryParams?.getAll('wagons[]')?.length ? queryParams.getAll('wagons[]').map(id => ({ id: Number(id) })) : [],
      idSearch: queryParams?.get('idSearch') || null,
      expireInDays: queryParams?.get('expireInDays') || null,
    };
    const { isValid, filters: normalizedFilters } = normalizeAndValidateDateRange(newFilters);

    if (!isValid) {
      showDateRangeWarning();
      return;
    }

    syncMainFiltersQuery(normalizedFilters);
    setAdvancedFilters(normalizedFilters);
    fetchData(normalizedFilters);
  }, []);

  function onChangeFilters(filters) {
    const { isValid, filters: normalizedFilters } = normalizeAndValidateDateRange({
      ...filters,
      page: 1,
    });

    if (!isValid) {
      showDateRangeWarning();
      return;
    }

    syncMainFiltersQuery(normalizedFilters);
    setAdvancedFilters(normalizedFilters);
    fetchData(normalizedFilters);
  }

  return (
    <ApplicationLayout
      title={"Relatório de checklist avulso manutenção"}
      contentFluid={true}
      RightComponent={
        <Button variant="secondary" onClick={() => setShowAdvancedFiltersModal(true)}>
          <Text weight={500} color="dark" type="regular">
            {handleTitleAdvancedFilters()}
          </Text>
        </Button>
      }
    >
      <>
        <div style={{ marginBottom: '3rem', paddingLeft: '140px', paddingRight: '140px' }}>
          <SingleChecklistResponseFilter
            loading={loading}
            onChange={onChangeFilters}
            setAdvancedFilters={setAdvancedFilters}
            advancedFilters={advancedFilters}
            rightAction={(
              <Button variant="primary" loading={loading || loadingExport} onClick={() => exportReport()}>
                <Text color="white" weight={500}>Exportar</Text>
              </Button>
            )}
          />
        </div>
        <DataTable
          data={data}
          loading={loading}
          withFilterOption={false}
          currentPage={pagination?.currentPage}
          pages={pagination?.lastPage}
          total={pagination?.total}
          onPageChanged={changePage}
          onPerPageChanged={changePerPage}
          defaultPerPage={advancedFilters?.per_page}
          perPageOptions={[{ v: 10 }, { v: 20 }, { v: 30 }, { v: 50 }]}
          tableStyles={{
            borderCollapse: 'collapse',
            '> thead > tr': {
              borderBottom: '2px solid #c5ccd5',
            },
            '> thead > tr > th': {
              padding: '5px 8px 7px',
              fontWeight: 'bold',
              fontSize: '16px',
              whiteSpace: 'nowrap',
            },
            '> thead > tr > th:nth-child(3)': {
              textAlign: 'center',
            },
            '> thead > tr > th:nth-child(4)': {
              textAlign: 'center',
            },
            '> thead > tr > th:nth-child(7)': {
              textAlign: 'center',
            },
            '> thead > tr > th:nth-child(9)': {
              textAlign: 'center',
            },
            '> thead > tr > th:nth-child(10)': {
              textAlign: 'center',
            },
            '> thead > tr > th:nth-child(11)': {
              textAlign: 'center',
            },
            '> tbody > tr': {
              borderBottom: '1px solid #cfd7e3',
              transition: 'background-color 0.15s ease',
            },
            '> tbody > tr:last-child': {
              borderBottom: '1px solid #cfd7e3',
            },
            '> tbody > tr:hover': {
              background: '#f3f6fa !important',
            },
            '> tbody > tr:hover > td': {
              background: '#f3f6fa !important',
            },
            '> tbody > tr > td': {
              padding: '9px 8px',
              whiteSpace: 'normal',
              lineHeight: 1.45,
              verticalAlign: 'top',
            },
          }}

          columns={[
            {
              name: 'ID', selector: row => (
                <Text size="small" fontSize="0.875rem" >
                  {row?.checklist_response_id || '-'}
                </Text>
              )
            },
            {
              name: 'Nome', selector: row => (row?.checklist_response_name || '-')
            },
            {
              name: 'Veículo', selector: row => (
                <Text size="small" fontSize="0.875rem">{row?.vehicle_plate || '-'}</Text>
              ),
              styles: { textAlign: 'center' },
            },
            {
              name: 'Implemento', selector: row => (
                <Text size="small" fontSize="0.875rem">{row?.wagon_plate || '-'}</Text>
              ),
              styles: { textAlign: 'center' },
            },
            {
              name: 'Criado por', selector: row => (
                <Text size="small" fontSize="0.875rem">{row?.created_by || '-'}</Text>
              )
            },
            {
              name: 'Criado em', selector: row => row?.created_at ? (
                <Text weight={600} fontSize="0.875rem">{moment(row.created_at).format('DD/MM/YYYY')}</Text>
              ) : '-'
            },
            { name: 'Status', selector: row => row?.status || '-', styles: { textAlign: 'center' } },
            {
              name: 'Respondido por', selector: row => (
                <Text size="small" fontSize="0.875rem">{row?.answered_by || '-'}</Text>
              )
            },
            {
              name: 'Respondido em', selector: row => row?.answered_at ? (
                <Text weight={600} fontSize="0.875rem">{moment(row.answered_at).format('DD/MM/YYYY')}</Text>
              ) : '-',
              styles: { textAlign: 'center' },
            },
            {
              name: 'Expira em', selector: row => row?.expiration_date && row?.expiration_date !== 'N/A' ? (
                <Text weight={600} fontSize="0.875rem">{moment(row.expiration_date).format('DD/MM/YYYY')}</Text>
              ) : '-',
              styles: { textAlign: 'center' },
            },
            {
              name: 'Dias para expirar', selector: row => row?.days_to_expire && row?.days_to_expire !== 'N/A' ? (
                <Text weight={600} fontSize="0.875rem">{row.days_to_expire <= 0 ? 'Expirado' : row.days_to_expire}</Text>
              ) : '-',
              styles: { textAlign: 'center' },
            },
          ]}
        />
      </>
      <AdvancedFilterModal
        onChange={onChangeFilters}
        loading={loading || loadingExport}
        showAdvancedFiltersModal={showAdvancedFiltersModal}
        setShowAdvancedFiltersModal={setShowAdvancedFiltersModal}
        setQtdAdvancedFilters={setQtdAdvancedFilters}
        setAdvancedFilters={setAdvancedFilters}
        advancedFilters={advancedFilters}
      />
    </ApplicationLayout>
  );
}
